import React, { useEffect, useState } from "react";
import FormSelect from "Common/Custom/FormSelect";
import FormSubmitButton from "Common/Custom/FormSubmitButton";
import PageBody from "Common/Custom/PageBody";
import { HeadingOne } from "Common/Custom/HeadingOne";
import useFetchAllData from "Hooks/useFetchAllData";
import { GetAllEmployeesForSetup, GetAllSalaryTypes, GetRateByEmployeeId } from "adminApi/SalarySetupApi/SalarySetup";

const AddSalarySetupForm = ({
    values = {},
    handleChange = () => { },
    setValues = () => { },
    handleSubmit = () => { },
    loading = false,
    errors = {},
    title = "Add Salary Setup"
}) => {
    const { data: employees } = useFetchAllData(GetAllEmployeesForSetup);
    const { data: salaryTypes } = useFetchAllData(GetAllSalaryTypes);

    console.log("employees", employees)

    const [allowances, setAllowances] = useState([]);
    const [deductions, setDeductions] = useState([]);
    const [grossSalary, setGrossSalary] = useState(0);

    // Initial data setup: pre-populate with default percentages from master data
    useEffect(() => {
        if (!salaryTypes) return;

        setAllowances(salaryTypes.filter(t => t.salary_type === "Allowance"));
        setDeductions(salaryTypes.filter(t => t.salary_type === "Deduction"));

        if (!values.salary_data || values.salary_data.length === 0) {
            const initialData = salaryTypes.map(t => ({
                salary_type_id: t.salary_type_id,
                amount: 0,
                percent: t.default_amount || 0,
                sal_name: t.sal_name,
                salary_type: t.salary_type
            }));
            setValues(prev => ({ ...prev, salary_data: initialData }));
        }
    }, [salaryTypes, setValues, values.salary_data?.length]);

    // Fetch Basic Rate from Employee Master when employee is selected
    useEffect(() => {
        if (!values.employee_id || !values.salary_data?.length) return;

        const fetchRate = async () => {
            try {
                const res = await GetRateByEmployeeId(values.employee_id);
                if (res?.data?.success && res.data.data) {
                    const basicRate = Number(res.data.data.rate || 0);
                    const payFrequency = res.data.data.pay_frequency;

                    const updatedData = values.salary_data.map(item => {
                        if (item.sal_name === "Basic") {
                            return { ...item, amount: basicRate.toFixed(2) };
                        }
                        const newAmount = (Number(item.percent || 0) / 100) * basicRate;
                        return { ...item, amount: newAmount.toFixed(2) };
                    });

                    setValues(prev => ({ ...prev, salary_data: updatedData, pay_frequency: payFrequency }));
                }
            } catch (err) {
                console.error("Error fetching employee rate:", err);
            }
        };
        fetchRate();
    }, [values.employee_id]);

    // Gross Salary Calculation Logic
    useEffect(() => {
        if (!values.salary_data?.length) return;

        const totalAllowance = values.salary_data
            .filter(item => item.salary_type === "Allowance")
            .reduce((sum, item) => sum + Number(item.amount || 0), 0);

        const totalDeduction = values.salary_data
            .filter(item => item.salary_type === "Deduction")
            .reduce((sum, item) => sum + Number(item.amount || 0), 0);

        setGrossSalary(totalAllowance - totalDeduction);
    }, [values.salary_data]);

    const handleValueChange = (typeId, field, val) => {
        if (!values.salary_data?.length) return;

        const basicItem = values.salary_data.find(item => item.sal_name === "Basic");
        const basicAmount = Number(basicItem?.amount || 0);
        const isBasicField = values.salary_data.find(i => i.salary_type_id === typeId)?.sal_name === "Basic";

        if (isBasicField) {
            const newBasic = Number(val);
            const updatedData = values.salary_data.map(item => {
                if (item.sal_name === "Basic") return { ...item, amount: val };
                const newAmount = (Number(item.percent || 0) / 100) * newBasic;
                return { ...item, amount: newAmount.toFixed(2) };
            });
            setValues(prev => ({ ...prev, salary_data: updatedData }));
        } else {
            const updatedData = values.salary_data.map(item => {
                if (item.salary_type_id === typeId) {
                    const percentage = Number(val);
                    const newAmount = (percentage / 100) * basicAmount;
                    return { ...item, percent: val, amount: newAmount.toFixed(2) };
                }
                return item;
            });
            setValues(prev => ({ ...prev, salary_data: updatedData }));
        }
    };

    const employeeOptions = (employees || []).map(emp => ({
        value: emp.employee_id,
        label: `${emp.setup_status ? "✓" : ""} ${emp.first_name} ${emp.last_name}`
    }));

    const selectedEmp = (employees || []).find(e => Number(e.employee_id) === Number(values.employee_id));

    const renderTable = (data, title, typeClass) => (
        <div className="card border-0 shadow-sm mb-4">
            <div className="card-header py-3 border-bottom-0">
                <h5 className="mb-0 fw-bold d-flex align-items-center gap-2">
                    <span className={`p-1 bg-${typeClass} rounded-circle`} style={{ width: '10px', height: '10px' }}></span>
                    {title}
                </h5>
            </div>
            <div className="card-body p-0">
                <div className="table-responsive">
                    <table className="table modern-table mb-0">
                        <thead>
                            <tr>
                                <th className="ps-4 py-3 text-muted small text-uppercase fw-bold">Salary Type</th>
                                <th className="py-3 text-muted small text-uppercase fw-bold text-center" colSpan={2}>Input % / ₹</th>
                                <th className="pe-4 py-3 text-muted small text-uppercase fw-bold text-end">Total Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map(type => {
                                const item = values.salary_data?.find(v => v.salary_type_id === type.salary_type_id);
                                const isBasic = type.sal_name === "Basic";

                                return (
                                    <tr key={type.salary_type_id} className="align-middle">
                                        <td className="ps-4 py-3 fw-semibold">{type.sal_name}</td>
                                        <td className="py-2 text-center" colSpan={2} style={{ minWidth: '180px' }}>
                                            <div className="input-group input-group-md border rounded-2 overflow-hidden">
                                                <input
                                                    type="number"
                                                    className="form-control border-0 bg-light text-center fw-bold"
                                                    value={isBasic ? item?.amount ?? "" : item?.percent ?? ""}
                                                    onChange={(e) => handleValueChange(type.salary_type_id, isBasic ? "amount" : "percent", e.target.value)}
                                                    disabled={isBasic}
                                                    placeholder="0"
                                                />
                                                <span className="input-group-text bg-light border-0 text-muted fw-bold">
                                                    {isBasic ? '₹' : '%'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="pe-4 py-3 text-end">
                                            <span className="fw-bold text-indigo fs-6">
                                                ₹{Number(item?.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr className="bg-light">
                                <td className="ps-4 py-3 fw-bold">Total {title}</td>
                                <td colSpan={2}></td>
                                <td className="pe-4 py-3 text-end text-primary">
                                    <h5 className="mb-0 fw-bold">
                                        ₹{data.reduce((sum, type) => {
                                            const item = values.salary_data?.find(v => v.salary_type_id === type.salary_type_id);
                                            return sum + Number(item?.amount || 0);
                                        }, 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                    </h5>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </div>
    );

    const isFormInvalid = !values.employee_id || grossSalary <= 0;

    return (
        <PageBody onSubmit={handleSubmit} headerchildren={<HeadingOne>{title}</HeadingOne>}>

            {/* Employee Selection Header */}
            <div className="col-12 mb-4">
                <div className="card shadow-sm border-0 rounded-3">
                    <div className="card-body p-4">
                        <div className="row g-4 align-items-center">
                            <div className="col-md-6 border-md-end">
                                <FormSelect
                                    field={{
                                        name: "employee_id",
                                        label: "Select Employee",
                                        required: true,
                                    }}
                                    value={values.employee_id}
                                    handleChange={handleChange}
                                    options={employeeOptions}
                                    error={errors.employee_id}
                                />
                            </div>
                            <div className="col-md-6">
                                {values.employee_id ? (
                                    <div className="ps-md-3">
                                        <span className="text-muted small text-uppercase fw-bold d-block mb-1">Pay Structure</span>
                                        <div className="d-flex align-items-center gap-2">
                                            <h5 className="mb-0 fw-bold text-primary">{values?.pay_frequency || 'N/A'}</h5>
                                            <i className="bi bi-info-circle text-muted"></i>
                                        </div>
                                        <span className="text-muted small">Auto-fetched from employee master</span>
                                    </div>
                                ) : (
                                    <div className="ps-md-3 text-muted italic">
                                        <i className="bi bi-person-badge me-2"></i>
                                        Select an employee to see pay structure details
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table Layout */}
            <div className="col-12 col-xl-6">
                {renderTable(allowances, "Allowances", "success")}
            </div>
            <div className="col-12 col-xl-6">
                {renderTable(deductions, "Deductions", "danger")}
            </div>

            {/* Gross Salary Summary Bar */}
            <div className="col-12 mb-4">
                <div className="card border-0 shadow-lg bg-dark text-white rounded-3 overflow-hidden">
                    <div className="card-body p-4">
                        <span className="text-white-50 text-uppercase small fw-bold d-block mb-2">Estimated Gross Salary</span>
                        <div className="d-flex align-items-baseline flex-wrap gap-2">
                            <h1 className="mb-0 fw-bold display-4">
                                ₹{grossSalary.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </h1>
                            <span className="text-white-50 fs-5">/ Month</span>
                        </div>
                    </div>
                </div>
            </div>

            <FormSubmitButton
                label="Create"
                loading={loading}
                disabled={isFormInvalid || selectedEmp?.setup_status}
                colSize="auto"
            />


        </PageBody>
    );
};

export default AddSalarySetupForm;
